import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { PropertyTable, buildAddressCell } from '../metadata/propertyConfig';
import { ListViewCell } from '../list-view-cell/ListViewCell';
import { ListViewTypes } from '../list-view-cell/model/ListViewCell';
import { TruncatedNotesCell } from '../list-view-cell/TruncatedNotesCell';
import { OrderCell } from '../../../orders/table-cell/OrderCell';
import { OrderCellAssets } from '../../../orders/table-cell/OrderCellAssets';
import { OrderStatusEnum } from '../../../orders/table-cell/model/orderCell';
import { OrderAmountCell } from '../cells/OrderAmountCell';
import { getPropertyAddress } from '../../../../services/property-address';
import { formatPriceDisplay } from '../../../../util/formatting';
import { Typography } from '@/components/ui/typography';
import {
  getServices,
  getCardNumber,
  capitalizeFirstLetter,
  getUnableToComplete,
  type OrderedServiceItem
} from './propertyListUtils';
import { ServicesOrderedCell } from '../cells/ServicesOrderedCell';
import { UtcBadge } from '../../../orders/table-cell/UtcBadge';
import { buildUtcDetailModel } from '../../../orders/table-cell/model/utcDetailModel';
import { buildServicesOrderedModel } from '../cells/servicesOrderedModel';
import type {
  OrderPropertyResponse,
  OrderResponse
} from '../../../../types/property.types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

dayjs.extend(localizedFormat);

// ---------------------------------------------------------------------------
// Appointment date column helper
// ---------------------------------------------------------------------------

const SHOW_APPOINTMENT_STATUSES = [
  OrderStatusEnum.SCHEDULED,
  OrderStatusEnum.PROCESSING,
  OrderStatusEnum.PROCESSED,
  OrderStatusEnum.APPOINTMENT_COMPLETE,
  OrderStatusEnum.UNABLE_TO_COMPLETE,
  OrderStatusEnum.DELIVERED,
  OrderStatusEnum.VERIFIED
];

function getAppointmentDate(data: Record<string, unknown>): string {
  const appointments = data.appointments as
    | Array<{
        cancelled?: boolean | null;
        dateStartLocal: string | null;
      }>
    | undefined;

  if (
    !SHOW_APPOINTMENT_STATUSES.some((s) => s === data.orderStatus) ||
    !appointments ||
    appointments.length === 0
  ) {
    return '-';
  }

  // Some appointment rows arrive with null date fields (legacy-migrated or
  // partially-cleared records). Treat them as no-date so we don't render
  // "Invalid Date" downstream.
  const valid = appointments.filter(
    (a): a is typeof a & { dateStartLocal: string } =>
      a.cancelled !== true && !!a.dateStartLocal
  );
  if (valid.length === 0) return '-';

  const sorted = [...valid].sort(
    (a, b) =>
      new Date(b.dateStartLocal).getTime() -
      new Date(a.dateStartLocal).getTime()
  );

  return dayjs(sorted[0].dateStartLocal).format('ddd, MMM D, YYYY');
}

// ---------------------------------------------------------------------------
// Payment method cell
// ---------------------------------------------------------------------------

function renderPaymentMethod(data: Record<string, unknown>) {
  if (data.paymentMethod == null) {
    return (
      <Typography variant="small-medium">Promo Code Applied</Typography>
    );
  }
  const pm = data.paymentMethod as { source?: string };
  if (pm.source === 'Monthly Invoice') {
    return <Typography variant="small-medium">Monthly Invoice</Typography>;
  }
  return (
    <Typography variant="small-medium">
      {capitalizeFirstLetter(data.cardType as string)}{' '}
      {getCardNumber(data.cardNumber as string)}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Order notes cell
// ---------------------------------------------------------------------------

function renderOrderNotes(
  orderId: string,
  noteText: string | null
) {
  if (!noteText) return { component: <span>-</span> };
  return {
    component: (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="max-w-[280px] truncate cursor-default">
              {noteText}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs">{noteText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  };
}

// ---------------------------------------------------------------------------
// processData — transforms API response rows into PropertyTable rows
// ---------------------------------------------------------------------------

export const processData = (
  apiData: { content: Record<string, unknown>[] },
  isProperty: boolean,
  onOpen?: (item: PropertyTable) => void,
): PropertyTable[] => {
  return apiData.content.map((data: Record<string, unknown>) => {
    const formattedAddress = getPropertyAddress(data);
    (data as Record<string, unknown>).formattedAddress = formattedAddress;

    if (data.rentReady === null) {
      data.rentReady = 'Not set';
    } else {
      data.rentReady = data.rentReady ? 'Yes' : 'No';
    }

    const orderNoteText =
      (data.assetNote as string | null) ??
      (data.specialInstructions as string | null) ??
      null;

    const services = data.services as OrderedServiceItem[] | undefined;

    // Band-aid: processData takes untyped rows for both lists, so the order
    // view is one boundary cast. Both cell sources are Picks of OrderResponse,
    // so a renamed field fails to compile there. Proper fix (typed rows from
    // usePropertyListData) is banked in docs/engineering-backlog.md.
    const order = data as unknown as OrderResponse;
    const servicesOrderedModel = buildServicesOrderedModel(order);
    const utcDetail = buildUtcDetailModel(
      order,
      servicesOrderedModel.originalServices
    );

    const property = data.property as OrderPropertyResponse | undefined;

    const hasPhotoset = isProperty
      ? (data.hasPhotoset as boolean | null | undefined)
      : (property?.hasPhotoset as boolean | null | undefined);

    // Build a partial row first so we have a typed item reference for
    // buildAddressCell. The cast at the end of the map ensures the full
    // PropertyTable shape — this is the same pattern used by the rest of
    // the existing code in this function. `hasPhotoset` is included here
    // (not only in the full return below) because the list view opens the
    // detail drawer with this partialRow via buildAddressCell/onOpen — the
    // drawer's Manage Assets button gates on `property.hasPhotoset`, so
    // omitting it hid the button in list view but not card view.
    const partialRow = {
      id: (data.propId || data.orderId) as string,
      apiResponseRaw: data,
      formattedAddress,
      hasPhotoset,
    } as PropertyTable;

    const defaultAddressCell = {
      component: (
        <ListViewCell
          type={ListViewTypes.PROPERTY_ADDRESS}
          address={formattedAddress}
        />
      ),
      data: formattedAddress,
    };

    return {
      id: partialRow.id,
      apiResponseRaw: data,
      propertyAddress: onOpen
        ? buildAddressCell(partialRow, onOpen, formattedAddress)
        : defaultAddressCell,
      lastOrderDate: {
        component: (
          <ListViewCell
            type={ListViewTypes.LAST_ORDER}
            lastOrder={data.orderNumber as string}
            orderDate={data.lastOrderDate as string}
            address={formattedAddress}
          />
        ),
        data: (data.lastOrderDate as string) ?? null
      },
      age: {
        component: (
          <ListViewCell type={ListViewTypes.AGE_OF_PHOTOS} age={null} />
        ),
        data: null
      },
      propertyStatus: data.propertyStatus,
      isActive: data.isActive === true ? 'Yes' : 'No',
      listingDate: data.listingDate,
      isListed: data.isListed === true ? 'Yes' : 'No',
      daysOnMarket: data.daysOnMarket,
      assignee: data.assignee,
      portfolioName: data.portfolioName,
      hasPhotos: data.hasPhotos ? 'Yes' : 'No',
      has3DTour: data.has3DTour ? 'Yes' : 'No',
      noticeGivenDate: (data.noticeGivenDate as string) ?? null,
      moveOutDate: (data.moveOutDate as string) ?? null,
      propertyType: data.propertyType,
      tenantStatus: data.tenantStatus,
      propertySource: data.propertySource,
      leaseStartDate: (data.leaseStartDate as string) ?? null,
      leaseEndDate: (data.leaseEndDate as string) ?? null,
      rentReady: data.rentReady as string | null,
      availableDate: (data.availableDate as string) ?? null,
      orderNumber: {
        component: (
          <ListViewCell
            type={ListViewTypes.ORDER_ID}
            orderId={` #${data.orderNumber}`}
            isOrderConfirm={false}
          />
        ),
        data: data.orderNumber
      },
      orderDate: (data.orderDate as string) ?? null,
      servicesOrdered: services
        ? {
            component: <ServicesOrderedCell model={servicesOrderedModel} />,
            data: getServices(services as { slug: string; name: string }[])
          }
        : services,
      'appointments.dateStartLocal': getAppointmentDate(data),
      orderCompletedDate: data.orderCompletedDate
        ? dayjs(data.orderCompletedDate as string).format('llll')
        : data.orderCompletedDate,
      orderStatus: {
        component: (
          <OrderCell
            data={data}
            badge={utcDetail && <UtcBadge detail={utcDetail} />}
          />
        ),
        data: data.orderStatus
      },
      asset: {
        component: (
          <OrderCellAssets
            data={data}
            isPropertySync={property?.propertySource ?? ''}
            pmsSource={property?.pmsSource ?? ''}
          />
        ),
        data: data.propertySource
      },
      // Surface the customer's PMS identifier only. Legacy/migrated rows
      // without `clientPropertyId` render an empty cell rather than the
      // backend UUID — matches develop's policy (PR #944).
      clientPropertyId: isProperty
        ? (data.clientPropertyId as string)
        : property?.clientPropertyId,
      clientUnitId: property?.clientUnitId,
      unableToComplete: getUnableToComplete(
        data.unableToComplete as boolean | null
      ),
      unableToCompleteReason: data.unableToCompleteReason,
      unableToCompleteNotes: {
        component: data.unableToCompleteNotes ? (
          <TruncatedNotesCell
            text={data.unableToCompleteNotes as string}
            tooltipId={`unable-to-complete-notes-tooltip-${data.orderId}`}
          />
        ) : (
          <span>-</span>
        )
      },
      accessType: (data.propertyAccess as Record<string, unknown>)?.accessType,
      onsiteContactName: (data.propertyAccess as Record<string, unknown>)
        ?.onsiteContactName,
      firstAvailableDate: data.firstAvailableDate
        ? dayjs(data.firstAvailableDate as string).format('MMMM D, YYYY')
        : data.firstAvailableDate,
      entity: data.entity,
      gateCode: (data.propertyAccess as Record<string, unknown>)?.gateCode,
      lockboxLocation: (data.propertyAccess as Record<string, unknown>)
        ?.lockboxLocation,
      downloadInvoice: '-',
      orderAmount: {
        component: (
          <OrderAmountCell
            amount={`$${formatPriceDisplay(data.propertyPrice as number | null | undefined)}`}
            orderNumber={data.orderNumber as string | null}
            invoiceAvailable={data.invoiceAvailable === true}
          />
        ),
        data: `$${formatPriceDisplay(data.propertyPrice as number | null | undefined)}`
      },
      orderedBy: data.orderedBy,
      orderMethod: data.orderMethod,
      paymentMethod: { component: renderPaymentMethod(data) },
      orderNotes: renderOrderNotes(
        data.orderId as string,
        orderNoteText
      ),
      rescheduleForUTCs: '-',
      formattedAddress,
      hasPhotoset
    } as unknown as PropertyTable;
  });
};
